// Global Instructions Rule Applied!

import { KanbanItem } from '../../interfaces'

/** Two sequences closer than this can no longer be split by a midpoint. */
const MIN_GAP = 0.001
/** Sequence for a card dropped into a column that has no other cards. */
const DEFAULT_SEQUENCE = 1000
/** Distance placed between a card and the end of the column it is dropped beyond. */
const END_STEP = 1

export interface ColumnMovePlan<T extends KanbanItem> {
  /** Sequence to persist for the moved card, or null when the drop leaves the column order as it is. */
  sequence: number | null
  /**
   * Other cards in the column whose sequence must change so the new order is
   * unambiguous. Empty when the drop lands in a usable gap.
   */
  renumbered: Array<{ item: T; sequence: number }>
}

type Sequence = number | null

function sequenceOf<T extends KanbanItem>(item: T, getPriority: (item: T) => number | null | undefined): Sequence {
  const value = getPriority(item)
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Plan the sequence writes for a card dropped at `targetIndex` of a column.
 *
 * `columnItems` is the target column exactly as displayed (sorted by sequence,
 * null sequences last). For a same-column move it still contains the moved
 * card, and `targetIndex` is the DropZone index over that displayed list, so
 * the index is corrected once the card is taken out of the list. Dropping a
 * card into its own slot (the zone directly above or below it) is a no-op.
 *
 * When the drop lands in a usable gap the moved card takes the midpoint of
 * its neighbours, or one step beyond the end of the column. When the
 * neighbours collide — equal sequences, a gap too small to split, or a card
 * without a sequence — the tied run around the drop is renumbered in even
 * steps between the nearest distinct neighbours, so the column order is
 * unambiguous after the writes.
 */
export function planColumnMove<T extends KanbanItem>(
  columnItems: T[],
  movedItem: T,
  targetIndex: number,
  getPriority: (item: T) => number | null | undefined
): ColumnMovePlan<T> {
  const currentIndex = columnItems.findIndex((item) => item.id === movedItem.id)
  const others = currentIndex === -1 ? columnItems : columnItems.filter((_, index) => index !== currentIndex)

  // The DropZone index counts the moved card while it is still in the list.
  let insertAt = targetIndex
  if (currentIndex !== -1 && currentIndex < targetIndex) insertAt -= 1
  insertAt = Math.max(0, Math.min(insertAt, others.length))

  if (currentIndex !== -1 && insertAt === currentIndex) {
    return { sequence: null, renumbered: [] }
  }

  const order: T[] = [...others.slice(0, insertAt), movedItem, ...others.slice(insertAt)]
  // The moved card's old sequence is stale; treat it as unknown.
  const sequences: Sequence[] = order.map((item, index) =>
    index === insertAt ? null : sequenceOf(item, getPriority)
  )
  const last = order.length - 1

  if (order.length === 1) {
    return { sequence: DEFAULT_SEQUENCE, renumbered: [] }
  }

  const above = insertAt > 0 ? sequences[insertAt - 1] : undefined
  const below = insertAt < last ? sequences[insertAt + 1] : undefined

  if (above === undefined && below != null) {
    return { sequence: below - END_STEP, renumbered: [] }
  }
  if (below === undefined && above != null) {
    return { sequence: above + END_STEP, renumbered: [] }
  }
  if (above != null && below != null && below - above >= 2 * MIN_GAP) {
    return { sequence: (above + below) / 2, renumbered: [] }
  }

  // Neighbours collide: renumber the tied run around the drop.
  const separable = (index: number): boolean => {
    const upper = sequences[index]
    const lower = sequences[index + 1]
    return upper != null && lower != null && lower - upper >= 2 * MIN_GAP
  }

  let runStart = insertAt
  while (runStart > 0 && !separable(runStart - 1)) runStart -= 1
  let runEnd = insertAt
  while (runEnd < last && !separable(runEnd)) runEnd += 1

  let lowerBound = runStart > 0 ? sequences[runStart - 1] : null
  let upperBound = runEnd < last ? sequences[runEnd + 1] : null
  let runLength = runEnd - runStart + 1

  if (lowerBound != null && upperBound != null && (upperBound - lowerBound) / (runLength + 1) < MIN_GAP) {
    // The run's bounds are too close together to fit it: renumber the whole column.
    runStart = 0
    runEnd = last
    lowerBound = null
    upperBound = null
    runLength = order.length
  }

  const sequenceAt = (position: number): number => {
    const offset = position - runStart + 1
    if (lowerBound != null && upperBound != null) {
      return lowerBound + ((upperBound - lowerBound) * offset) / (runLength + 1)
    }
    if (lowerBound != null) return lowerBound + offset * END_STEP
    if (upperBound != null) return upperBound - (runLength - offset + 1) * END_STEP
    return offset
  }

  const renumbered: ColumnMovePlan<T>['renumbered'] = []
  for (let position = runStart; position <= runEnd; position += 1) {
    if (position === insertAt) continue
    const sequence = sequenceAt(position)
    if (sequences[position] !== sequence) {
      renumbered.push({ item: order[position], sequence })
    }
  }

  return { sequence: sequenceAt(insertAt), renumbered }
}
